import { useState, useCallback, useEffect, useRef } from 'react';
import { SceneWithUserNames, UpdateSceneParams } from '../../../../types/scene.types';
import { useSceneUpdates } from '../../../../hooks/dataUpdates/useSceneUpdates';

export interface FindReplacePosition {
  top: number;
  left: number;
}

export const useFindReplace = (
  scenes: SceneWithUserNames[],
  setScenes: React.Dispatch<React.SetStateAction<SceneWithUserNames[]>>,
  setSceneLocationDisplayTexts?: React.Dispatch<React.SetStateAction<{ [sceneId: string]: string }>>
) => {
  // 获取 breakdownId（从第一个 scene 中获取）
  const breakdownId = scenes.length > 0 ? scenes[0].sceneBelongBreakdownID : undefined;
  const { updateScenesData } = useSceneUpdates();
  
  // 查找替换组件状态
  const [findReplaceVisible, setFindReplaceVisible] = useState(false);
  const [findReplaceColumn, setFindReplaceColumn] = useState<string>('');
  const [findReplacePosition, setFindReplacePosition] = useState<FindReplacePosition>({ top: 0, left: 0 });
  const triggerButtonRef = useRef<HTMLButtonElement | null>(null);

  const computeDialogPosition = useCallback((anchorRect: DOMRect): FindReplacePosition => {
    const findReplaceWidth = 600;
    const viewportPadding = 8;
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;

    const centeredLeft = anchorRect.left + (anchorRect.width / 2) - (findReplaceWidth / 2);
    const minLeft = viewportPadding;
    const maxLeft = Math.max(viewportPadding, viewportWidth - findReplaceWidth - viewportPadding);
    const left = Math.min(Math.max(centeredLeft, minLeft), maxLeft);
    const top = anchorRect.bottom + 4;

    return { top, left };
  }, []);

  // 打开查找替换弹窗
  const handleOpenFindReplace = useCallback((columnKey: string, event: React.MouseEvent<HTMLButtonElement>) => {
    triggerButtonRef.current = event.currentTarget;
    setFindReplacePosition(computeDialogPosition(event.currentTarget.getBoundingClientRect()));
    setFindReplaceColumn(columnKey);
    setFindReplaceVisible(true);
  }, [computeDialogPosition]);

  // 关闭查找替换弹窗
  const handleCloseFindReplace = useCallback(() => {
    setFindReplaceVisible(false);
    setFindReplaceColumn('');
    triggerButtonRef.current = null;
  }, []);

  // 查找替换数据更新处理
  // 统一流程：只调用 updateScenesData，它会更新缓存并调用服务器 API，缓存更新后会自然同步到 UI
  const handleFindReplaceUpdate = useCallback(async (updatedData: string[]) => {
    if (!breakdownId) {
      console.error('查找替换失败：缺少 breakdownId');
      return;
    }
    
    // 准备批量更新到服务器的数据
    let updates: Array<{ sceneId: string; data: UpdateSceneParams }> = [];
    
    if (findReplaceColumn === 'scenePlotDay') {
      updates = scenes
        .map((scene, index) => {
          if (index < updatedData.length && scene.scenePlotDay !== updatedData[index]) {
            return {
              sceneId: scene.sceneID,
              data: { scenePlotDay: updatedData[index] } as UpdateSceneParams,
            };
          }
          return null;
        })
        .filter(Boolean) as Array<{ sceneId: string; data: UpdateSceneParams }>;
    } else if (findReplaceColumn === 'sceneSet') {
      updates = scenes
        .map((scene, index) => {
          if (index < updatedData.length && scene.sceneSet !== updatedData[index]) {
            return {
              sceneId: scene.sceneID,
              data: { sceneSet: updatedData[index] } as UpdateSceneParams,
            };
          }
          return null;
        })
        .filter(Boolean) as Array<{ sceneId: string; data: UpdateSceneParams }>;
    } else if (findReplaceColumn === 'scenePlot') {
      updates = scenes
        .map((scene, index) => {
          if (index < updatedData.length && scene.scenePlot !== updatedData[index]) {
            return {
              sceneId: scene.sceneID,
              data: { scenePlot: updatedData[index] } as UpdateSceneParams,
            };
          }
          return null;
        })
        .filter(Boolean) as Array<{ sceneId: string; data: UpdateSceneParams }>;
    } else if (findReplaceColumn === 'sceneLocation') {
      updates = scenes
        .map((scene, index) => {
          if (index < updatedData.length && scene.sceneLocation !== updatedData[index]) {
            return {
              sceneId: scene.sceneID,
              data: { sceneLocation: updatedData[index] } as UpdateSceneParams,
            };
          }
          return null;
        })
        .filter(Boolean) as Array<{ sceneId: string; data: UpdateSceneParams }>;
      
      // 同步更新地点显示文本缓存（在缓存更新之前，提供即时反馈）
      if (setSceneLocationDisplayTexts) {
        const updatedDisplayTexts: { [sceneId: string]: string } = {};
        scenes.forEach((scene, index) => {
          if (index < updatedData.length) {
            updatedDisplayTexts[scene.sceneID] = updatedData[index] || '';
          }
        });
        setSceneLocationDisplayTexts(prev => ({
          ...prev,
          ...updatedDisplayTexts
        }));
      }
    }
    
    // 调用批量更新 API（updateScenesData 会更新缓存并调用服务器 API）
    if (updates.length > 0) {
      try {
        await updateScenesData(updates, breakdownId);
        console.log(`批量更新${findReplaceColumn}成功:`, updates.length, '个场次');
      } catch (error) {
        console.error(`批量更新${findReplaceColumn}失败:`, error);
        // 回滚地点显示文本缓存（如果更新了 sceneLocation）
        if (findReplaceColumn === 'sceneLocation' && setSceneLocationDisplayTexts) {
          const previousDisplayTexts: { [sceneId: string]: string } = {};
          scenes.forEach(scene => {
            previousDisplayTexts[scene.sceneID] = scene.sceneLocation || '';
          });
          setSceneLocationDisplayTexts(prev => ({
            ...prev,
            ...previousDisplayTexts
          }));
        }
        throw error;
      }
    }
  }, [findReplaceColumn, scenes, breakdownId, setSceneLocationDisplayTexts, updateScenesData]);

  // 获取列数据
  const getColumnData = useCallback((columnKey: string): string[] => {
    return scenes.map(scene => {
      if (columnKey === 'scenePlotDay') {
        return scene.scenePlotDay || '';
      } else if (columnKey === 'sceneSet') {
        return scene.sceneSet || '';
      } else if (columnKey === 'scenePlot') {
        return scene.scenePlot || '';
      } else if (columnKey === 'sceneLocation') {
        return scene.sceneLocation || '';
      }
      return '';
    });
  }, [scenes]);

  // 当查找替换组件可见时，监听滚动事件更新位置
  useEffect(() => {
    if (!findReplaceVisible) return;

    const updatePosition = () => {
      if (triggerButtonRef.current) {
        setFindReplacePosition(computeDialogPosition(triggerButtonRef.current.getBoundingClientRect()));
      }
    };

    // 初始更新
    updatePosition();

    // 监听滚动事件
    const breakdownTableContainer = document.querySelector('.breakdown-table-container');
    const excelTableContainer = document.querySelector('.excel-table-container');

    breakdownTableContainer?.addEventListener('scroll', updatePosition);
    excelTableContainer?.addEventListener('scroll', updatePosition);
    window.addEventListener('scroll', updatePosition);
    window.addEventListener('resize', updatePosition);

    return () => {
      breakdownTableContainer?.removeEventListener('scroll', updatePosition);
      excelTableContainer?.removeEventListener('scroll', updatePosition);
      window.removeEventListener('scroll', updatePosition);
      window.removeEventListener('resize', updatePosition);
    };
  }, [computeDialogPosition, findReplaceVisible]);

  return {
    findReplaceVisible,
    findReplaceColumn,
    findReplacePosition,
    handleOpenFindReplace,
    handleCloseFindReplace,
    handleFindReplaceUpdate,
    getColumnData
  };
};